import React, { FunctionComponent, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useI18n } from "../../../hooks/useI18n";
import { AppState } from "../../../store";
import { thunkFetchStoreWasteStatistics } from "../../../store/statistics/waste/actions";
import { StoreWasteStatisticsState } from "../../../store/statistics/waste/types";
import StatTable from "../components/StatTable";
import DateRangePicker from "../../../components/inputs/daterangepicker";
import TableWrapper from "../components/TableWrapper";
import { Grid, useMediaQuery } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import moment from "moment";
import BarChart from "../components/BarChart";
import { BarChartData } from "../components/BarChart/types";
import WasteDetailsTable from "./WasteDetailsTable";
import { Market } from "../../../models/Market";
import { Currency } from "../../../components/misc/Currency";
import { ExclVat } from "../components/exclVat";
import { Waste } from "../../../models/Statistics";

export const WasteStatistics: FunctionComponent = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("xs"));
  const { t } = useI18n();

  const [dateRange, setDateRange] = useState([
    moment().toDate(),
    moment().toDate()
  ]);

  const statisticsState = useSelector<AppState, StoreWasteStatisticsState>(
    s => s.wasteStatistics
  );

  let { storeWasteStatistics, isLoading } = statisticsState;

  useEffect(() => {
    if (dateRange) {
      dispatch(thunkFetchStoreWasteStatistics(dateRange[0], dateRange[1]));
    }
  }, [dateRange]);

  const populateWasteGroupedByProp = (
    waste: any[],
    prop: string
  ): Map<string, number> => {
    return waste.reduce((r, o) => {
      // @ts-ignore
      if (o[prop] == null) return r;
      // @ts-ignore
      let cost = r.get(o[prop]) || 0;
      cost += o.cost;
      // @ts-ignore
      return r.set(o[prop], cost);
    }, new Map());
  };

  const populateTotalWaste = (chartType: string) => {
    const totalWaste: Waste[] = [];
    storeWasteStatistics.forEach(s => {
      totalWaste.push(...s.wastes);
    });

    return populateWasteGroupedByProp(totalWaste, chartType);
  };

  const populateChartData = (chartType: string): BarChartData => {
    const waste = populateTotalWaste(chartType);
    return {
      labels: [...waste.keys()],
      datasets: [
        {
          label: "",
          data: [...waste.values()]
        }
      ]
    };
  };

  return (
    <>
      <DateRangePicker
        disabled={isLoading}
        onChange={setDateRange}
        value={dateRange}
      />
      <ExclVat />
      <Grid
        container
        direction={"row"}
        justifyContent={"space-evenly"}
        alignItems={"flex-start"}
        style={{ marginTop: "2rem" }}
        spacing={2}
      >
        <Grid container item xs={12} lg={7} spacing={2}>
          <Grid item xs={12}>
            <TableWrapper title={t("common.statistics.wasteStatistics")}>
              <StatTable
                isLoading={isLoading}
                exportName={t("common.statistics.wasteStatistics")}
                columns={[
                  {
                    title: "Store",
                    field: "storeName"
                  },
                  {
                    title: t("common.tableHeaders.totalWaste"),
                    field: "wasteCostFormatted",
                    customSort: (a: any, b: any) => {
                      return a.wasteCost - b.wasteCost;
                    }
                  },
                  {
                    title: t("common.tableHeaders.grossSales"),
                    field: "totalSalesFormatted",
                    hidden: mobile,
                    customSort: (a: any, b: any) => {
                      return a.totalSales - b.totalSales;
                    }
                  },
                  {
                    title: "Waste",
                    field: "waste",
                    customSort: (a: any, b: any) => {
                      let x = a.waste.slice(0, -1);
                      let y = b.waste.slice(0, -1);
                      return parseInt(x) - parseInt(y);
                    }
                  },
                  {
                    title: "Theft",
                    field: "theft",
                    customSort: (a: any, b: any) => {
                      let x = a.theft.slice(0, -1);
                      let y = b.theft.slice(0, -1);
                      return parseInt(x) - parseInt(y);
                    }
                  }
                ]}
                data={
                  !isLoading
                    ? storeWasteStatistics.map(w => {
                        return {
                          storeId: w.storeId,
                          storeName: w.storeName,
                          wasteCostFormatted: <Currency value={w.totalWaste} />,
                          totalSalesFormatted: (
                            <Currency value={w.totalSales} />
                          ),
                          wasteCost: w.totalWaste,
                          waste: `${Math.floor(
                            ((w.totalWaste - w.totalTheft) * 100) / w.totalSales
                          )}%`,
                          theft: `${Math.floor(
                            (w.totalTheft * 100) / w.totalSales
                          )}%`,
                          wastePerReason: populateWasteGroupedByProp(
                            w.wastes,
                            "reason"
                          ),
                          wastePerCategory: populateWasteGroupedByProp(
                            w.wastes,
                            "category"
                          ),
                          totalWaste: w.totalWaste,
                          totalSales: w.totalSales
                        };
                      })
                    : []
                }
                detailPanel={[
                  {
                    render: (rowData: any) => (
                      <WasteDetailsTable
                        wastePerReason={rowData.wastePerReason}
                        wastePerCategory={rowData.wastePerCategory}
                        totalWaste={rowData.totalWaste}
                        productWaste={{
                          storeId: rowData.storeId,
                          periodFrom: dateRange[0],
                          periodTo: dateRange[1]
                        }}
                      />
                    )
                  }
                ]}
                hideDetailPanel={mobile}
              />
            </TableWrapper>
          </Grid>
          <Grid item xs={12}>
            <BarChart
              title={"Sales vs waste"}
              chartData={{
                labels: storeWasteStatistics.map(w => w.storeName),
                datasets: [
                  {
                    label: t("common.tableHeaders.totalSales"),
                    rgbColor: { r: 58, g: 160, b: 75 },
                    data: storeWasteStatistics.map(w => w.totalSales)
                  },
                  {
                    label: t("common.tableHeaders.totalWaste"),
                    rgbColor: { r: 5, g: 89, b: 141 },
                    data: storeWasteStatistics.map(w => w.totalWaste)
                  },
                  {
                    label: t("common.messages.totalSpoilage"),
                    rgbColor: { r: 140, g: 155, b: 13 },
                    data: storeWasteStatistics.map(
                      w => w.totalWaste - w.totalTheft
                    )
                  },
                  {
                    label: t("common.messages.totalTheft"),
                    rgbColor: { r: 195, g: 88, b: 66 },
                    data: storeWasteStatistics.map(w => w.totalTheft)
                  }
                ]
              }}
              showLegend={true}
              isLoading={isLoading}
            />
          </Grid>
        </Grid>
        <Grid container item xs={12} lg={5} spacing={2}>
          <Grid item xs={12}>
            <BarChart
              title={"Waste by category"}
              chartData={populateChartData("category")}
              showLegend={false}
              isLoading={isLoading}
            />
          </Grid>
          <Grid item xs={12}>
            <BarChart
              title={"Waste by reason"}
              chartData={populateChartData("reason")}
              showLegend={false}
              isLoading={isLoading}
            />
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};
